import React, { ComponentProps, memo, useState } from 'react'

import InputMask from '@mona-health/react-input-mask'
import { equals } from 'ramda'

import { useHandler, useMemoizedCall } from '@surglogs-app/hooks'

import { Input } from './Input'

export const timeRegex =
  /^$|^[01]\d:[0-5]\d$|^2[0-3]:[0-5]\d$|^\d:[0-5]\d$|^__:__$/

const digit = /[0-9]/
const firstHourDigit = /[0,1,2]/
const secondHourDigitAfterTwo = /[0,1,2,3]/
const firstMinuteDigit = /[0,1,2,3,4,5]/
const secondaryMaskInitialValue = '2_:__'

const primaryMask = [firstHourDigit, digit, ':', firstMinuteDigit, digit]
const secondaryMask = [
  firstHourDigit,
  secondHourDigitAfterTwo,
  ':',
  firstMinuteDigit,
  digit,
]
const tertiaryMask = [digit, ':', firstMinuteDigit, digit]

const deriveDefaultValue = (value: string | undefined) => {
  const v = String(value)
  if (!timeRegex.test(v)) {
    return ''
  }
  return v[1] === ':' ? `0${v}` : v
}

const handleChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  mask: (string | RegExp)[],
  setMask: (value: (string | RegExp)[]) => void,
  setInputValue: (value: string) => void,
  onChangeProp: (value: string) => void,
) => {
  const { value } = e.target

  if (value[0] === '2' && !secondHourDigitAfterTwo.test(value[1])) {
    const newValue = `${value.slice(0, 1)}_${value.slice(2)}`
    setInputValue(newValue)
    onChangeProp(newValue)
    if (!equals(mask, secondaryMask)) {
      setMask(secondaryMask)
    }
    return
  }

  if (equals(mask, secondaryMask) && value[0] !== '2') {
    setMask(primaryMask)
    setInputValue(value)
    onChangeProp(value)
    return
  }

  if (value === secondaryMaskInitialValue) {
    if (!equals(mask, secondaryMask)) {
      setMask(secondaryMask)
    }
    setInputValue(value)
    onChangeProp(value)
    return
  }

  if (value.slice(0, 2) === '_:') {
    if (!equals(mask, primaryMask)) {
      setMask(primaryMask)
    }
    setInputValue(`__:${value.slice(2)}`)
    onChangeProp(`__:${value.slice(2)}`)
    return
  }

  setInputValue(value)
  onChangeProp(value)
}

const handleKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  inputValue: string,
  setMask: (value: (string | RegExp)[]) => void,
) => {
  if (
    inputValue.slice(0, 2) === '__' &&
    !firstHourDigit.test(e.key) &&
    !Number.isNaN(Number(e.key))
  ) {
    setMask(tertiaryMask)
  }
}

interface TimeInputProps
  extends Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
}

export const TimeInput = memo(
  ({
    disabled,
    value: valueProp,
    onChange: onChangeProp,
    ...rest
  }: TimeInputProps) => {
    const [mask, setMask] = useState(primaryMask)
    const value = useMemoizedCall(deriveDefaultValue, [valueProp])
    const [inputValue, setInputValue] = useState(value)

    const onChange = useHandler((e: React.ChangeEvent<HTMLInputElement>) =>
      handleChange(e, mask, setMask, setInputValue, onChangeProp),
    )
    const onKeyDown = useHandler((e: React.KeyboardEvent<HTMLInputElement>) =>
      handleKeyDown(e, inputValue, setMask),
    )

    return (
      <Input
        {...{
          as: InputMask,
          value: inputValue,
          onChange,
          onKeyDown,
          disabled,
          mask: !disabled ? mask : undefined,
        }}
        {...rest}
      />
    )
  },
)
